import { useState, useCallback, useRef, type ReactNode, type FormEvent, type ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

interface FirstProductStepProps {
  readonly onComplete: (data: { name: string; price: number; description: string; image: File | null }) => void;
}

export function FirstProductStep({ onComplete }: FirstProductStepProps): ReactNode {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const inputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setImage(file);
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    }
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const newErrors: Record<string, string> = {};
      if (!name.trim()) newErrors["name"] = "\u05E9\u05DD \u05D4\u05DE\u05D5\u05E6\u05E8 \u05D7\u05D5\u05D1\u05D4";
      const priceNum = Number(price);
      if (!price || priceNum <= 0) newErrors["price"] = "\u05D4\u05D6\u05D9\u05E0\u05D5 \u05DE\u05D7\u05D9\u05E8 \u05EA\u05E7\u05D9\u05DF";
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      setErrors({});
      onComplete({
        name: name.trim(),
        price: Math.round(priceNum * 100), // NIS to agorot
        description: description.trim(),
        image,
      });
    },
    [name, price, description, image, onComplete],
  );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 px-4 py-6">
      <h2 className="text-xl font-bold">{"\u05D4\u05DE\u05D5\u05E6\u05E8 \u05D4\u05E8\u05D0\u05E9\u05D5\u05DF \u05E9\u05DC\u05DB\u05DD"}</h2>
      <p className="text-sm text-muted-foreground">
        {"\u05D4\u05D5\u05E1\u05D9\u05E4\u05D5 \u05DE\u05D5\u05E6\u05E8 \u05D0\u05D7\u05D3 \u05DC\u05D4\u05EA\u05D7\u05DC\u05D4. \u05EA\u05D5\u05DB\u05DC\u05D5 \u05DC\u05D4\u05D5\u05E1\u05D9\u05E3 \u05E2\u05D5\u05D3 \u05DE\u05D4\u05E4\u05D0\u05E0\u05DC \u05D1\u05D4\u05DE\u05E9\u05DA."}
      </p>

      <Card>
        <CardContent className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="productName" className="text-sm font-medium">
              {"\u05E9\u05DD \u05D4\u05DE\u05D5\u05E6\u05E8"}
            </label>
            <Input
              id="productName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="min-h-[44px] text-base"
              placeholder={"\u05E9\u05DD \u05D4\u05DE\u05D5\u05E6\u05E8"}
            />
            {errors["name"] !== undefined && (
              <p className="text-sm text-destructive">{errors["name"]}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="productPrice" className="text-sm font-medium">
              {"\u05DE\u05D7\u05D9\u05E8 (\u20AA)"}
            </label>
            <Input
              id="productPrice"
              type="number"
              dir="ltr"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="min-h-[44px] text-base"
              placeholder="0.00"
            />
            {errors["price"] !== undefined && (
              <p className="text-sm text-destructive">{errors["price"]}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="productDesc" className="text-sm font-medium">
              {"\u05EA\u05D9\u05D0\u05D5\u05E8"}
            </label>
            <textarea
              id="productDesc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="min-h-[44px] w-full rounded-md border bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder={"\u05EA\u05D9\u05D0\u05D5\u05E8 \u05E7\u05E6\u05E8 \u05E9\u05DC \u05D4\u05DE\u05D5\u05E6\u05E8"}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">{"\u05EA\u05DE\u05D5\u05E0\u05D4"}</label>
            {imagePreview !== null ? (
              <div className="flex items-center gap-3">
                <img src={imagePreview} alt="Product" className="h-20 w-20 rounded border object-cover" />
                <Button type="button" variant="outline" size="sm" className="min-h-[44px]" onClick={() => inputRef.current?.click()}>
                  {"\u05D4\u05D7\u05DC\u05E3"}
                </Button>
              </div>
            ) : (
              <Button type="button" variant="outline" className="min-h-[44px]" onClick={() => inputRef.current?.click()}>
                {"\u05D4\u05E2\u05DC\u05D0\u05EA \u05EA\u05DE\u05D5\u05E0\u05D4"}
              </Button>
            )}
            <input ref={inputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
          </div>
        </CardContent>
      </Card>

      <Button type="submit" size="lg" className="min-h-[44px]">
        {"\u05D4\u05DE\u05E9\u05DA"}
      </Button>
    </form>
  );
}
